// MFASettings.js - MFA configuration component for Settings page
// Allows users to enable/disable MFA methods

import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getMFAStatus, enableMFA, verifyMFA, disableMFA } from '../../services/mfaService';
import './MFASettings.css';

const MFASettings = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(null); // 'email' | 'authenticator' | null
    const [qrData, setQrData] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await getMFAStatus();
            setStatus(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const handleEnable = async (method) => {
        try {
            setEnrolling(method);
            setError(null);
            setSuccess(null);
            setActionLoading(true);

            const result = await enableMFA(method);

            if (method === 'authenticator') {
                // Show QR code for scanning
                setQrData(result);
                setActionLoading(false);
            } else {
                // Email MFA enabled immediately
                await loadStatus();
                setEnrolling(null);
                setSuccess('Email MFA enabled successfully');
                setActionLoading(false);
            }
        } catch (err) {
            setError(err.message);
            setEnrolling(null);
            setActionLoading(false);
        }
    };

    const handleVerify = async () => {
        try {
            setError(null);
            setActionLoading(true);

            await verifyMFA(enrolling, verificationCode);
            await loadStatus();

            setEnrolling(null);
            setQrData(null);
            setVerificationCode('');
            setSuccess('Authenticator MFA enabled successfully');
            setActionLoading(false);
        } catch (err) {
            setError('Invalid code. Please try again.');
            setActionLoading(false);
        }
    };

    const handleDisable = async (method, methodId) => {
        if (!window.confirm('Are you sure you want to disable this MFA method? Your account will be less secure.')) {
            return;
        }

        try {
            setError(null);
            setSuccess(null);
            setActionLoading(true);

            await disableMFA(method, methodId);
            await loadStatus();

            setSuccess(`${method} MFA method has been disabled`);
            setActionLoading(false);
        } catch (err) {
            setError(err.message);
            setActionLoading(false);
        }
    };

    const handleCancelEnrollment = () => {
        setQrData(null);
        setEnrolling(null);
        setVerificationCode('');
        setError(null);
    };

    const getMethodDisplayName = (type) => {
        const names = {
            'email': 'Email OTP',
            'softwareOath': 'Authenticator App',
            'microsoftAuthenticator': 'Microsoft Authenticator',
            'phone': 'Phone'
        };
        return names[type] || type;
    };

    if (loading) {
        return (
            <div className="mfa-settings">
                <div className="mfa-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading MFA settings...</span>
                </div>
            </div>
        );
    }

    // Check if configuration is required (Azure AD not set up)
    if (status?.configurationRequired) {
        return (
            <div className="mfa-settings">
                <h3>Two-Factor Authentication</h3>
                <div className="mfa-config-notice">
                    <span className="notice-icon">!</span>
                    <div className="notice-content">
                        <strong>Configuration Required</strong>
                        <p>MFA requires Azure AD configuration. Please contact your administrator.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mfa-settings">
            <h3>Two-Factor Authentication</h3>
            <p className="mfa-description">
                Add an extra layer of security to your account by requiring a second
                verification step when signing in.
            </p>

            {error && (
                <div className="mfa-error">
                    <span className="error-icon">!</span>
                    {error}
                </div>
            )}

            {success && (
                <div className="mfa-success">
                    <span className="success-icon">&#10003;</span>
                    {success}
                </div>
            )}

            {!status?.mfaEnabled ? (
                // Not enrolled - show enable options
                <div className="mfa-options">
                    <div className="mfa-option">
                        <div className="mfa-option-icon email-icon">&#9993;</div>
                        <div className="mfa-option-info">
                            <h4>Email Verification</h4>
                            <p>Receive a one-time code via email when signing in</p>
                        </div>
                        <button
                            onClick={() => handleEnable('email')}
                            disabled={enrolling || actionLoading}
                            className="btn-mfa-enable"
                        >
                            {enrolling === 'email' && actionLoading ? 'Setting up...' : 'Enable'}
                        </button>
                    </div>

                    <div className="mfa-option">
                        <div className="mfa-option-icon auth-icon">&#128274;</div>
                        <div className="mfa-option-info">
                            <h4>Authenticator App</h4>
                            <p>Use Google Authenticator, Microsoft Authenticator, or Authy</p>
                        </div>
                        <button
                            onClick={() => handleEnable('authenticator')}
                            disabled={enrolling || actionLoading}
                            className="btn-mfa-enable"
                        >
                            {enrolling === 'authenticator' && !qrData ? 'Setting up...' : 'Enable'}
                        </button>
                    </div>
                </div>
            ) : (
                // Enrolled - show current methods
                <div className="mfa-enabled">
                    <div className="mfa-status-badge">
                        <span className="badge-icon">&#128274;</span>
                        MFA Enabled
                    </div>
                    <div className="mfa-methods-list">
                        {status.methods.map((method, idx) => (
                            <div key={idx} className="mfa-method-item">
                                <div className="method-info">
                                    <span className="method-icon">
                                        {method.type === 'email' ? '✉' : '🔐'}
                                    </span>
                                    <span className="method-type">{getMethodDisplayName(method.type)}</span>
                                    {method.details?.emailAddress && (
                                        <span className="method-detail">{method.details.emailAddress}</span>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleDisable(method.type, method.id)}
                                    disabled={actionLoading}
                                    className="btn-mfa-disable"
                                >
                                    {actionLoading ? '...' : 'Disable'}
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Allow adding additional method if only one is enabled */}
                    {status.methods.length < 2 && (
                        <div className="add-method-section">
                            <p className="add-method-hint">Add another method for backup access</p>
                            <div className="add-method-buttons">
                                {!status.methods.find(m => m.type === 'email') && (
                                    <button
                                        onClick={() => handleEnable('email')}
                                        disabled={enrolling || actionLoading}
                                        className="btn-add-method"
                                    >
                                        + Add Email
                                    </button>
                                )}
                                {!status.methods.find(m => m.type === 'softwareOath') && (
                                    <button
                                        onClick={() => handleEnable('authenticator')}
                                        disabled={enrolling || actionLoading}
                                        className="btn-add-method"
                                    >
                                        + Add Authenticator
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Authenticator Enrollment Modal */}
            {qrData && (
                <div className="mfa-enrollment-modal">
                    <div className="mfa-enrollment-overlay" onClick={handleCancelEnrollment}></div>
                    <div className="mfa-enrollment-content">
                        <h4>Set up Authenticator App</h4>
                        <div className="enrollment-steps">
                            <div className="step">
                                <span className="step-number">1</span>
                                <span>Download an authenticator app</span>
                            </div>
                            <div className="app-suggestions">
                                <span className="app-tag">Google Authenticator</span>
                                <span className="app-tag">Microsoft Authenticator</span>
                                <span className="app-tag">Authy</span>
                            </div>
                            <div className="step">
                                <span className="step-number">2</span>
                                <span>Scan this QR code</span>
                            </div>
                        </div>

                        <div className="qr-container">
                            <QRCodeSVG
                                value={qrData.qrCodeUrl}
                                size={200}
                                level="M"
                                includeMargin={true}
                                bgColor="#ffffff"
                                fgColor="#000000"
                            />
                        </div>

                        <div className="secret-key-section">
                            <p>Or enter this code manually:</p>
                            <code className="secret-key">{qrData.secretKey}</code>
                        </div>

                        <div className="step">
                            <span className="step-number">3</span>
                            <span>Enter the 6-digit code from your app</span>
                        </div>

                        <div className="verification-input">
                            <input
                                type="text"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="000000"
                                maxLength={6}
                                className="code-input"
                                autoComplete="off"
                            />
                            <button
                                onClick={handleVerify}
                                disabled={verificationCode.length !== 6 || actionLoading}
                                className="btn-verify"
                            >
                                {actionLoading ? 'Verifying...' : 'Verify & Enable'}
                            </button>
                        </div>

                        <button className="btn-cancel" onClick={handleCancelEnrollment}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MFASettings;
